/*
 * BF Gift Block — gift selection, pricing breakdown, delivery timer.
 *
 * Pricing formulas (verified against client sheet):
 *   1. set_value      = Σ compare-at of core + selected gift + checked upsells
 *   2. bundle_savings = core compare-at − core MSRP (only when core is a set; hidden when 0)
 *   3. bf_discount    = row1 − row2 − row4
 *   4. total          = core price + Σ checked upsell prices (gift counts as 0)
 *   5. savings        = row1 − row4 (percent vs row1)
 *   incl              = selected gift name + its compare-at
 *
 * The selected gift is exposed through [data-bf-gift-output] as JSON
 * ({id, properties:{_bf_gift:"true", ...}}) — ProductForm (theme.js) appends
 * it to the cart/add.js items.
 */
(function () {
  const formatMoney = (cents) => {
    const locale = (window.Shopify?.locale || document.documentElement.lang || '').toLowerCase();
    if (locale.startsWith('fr') || locale.startsWith('de')) {
      return (cents / 100).toFixed(2).replace('.', ',') + '€';
    }
    const format = window.themeVariables?.settings?.moneyFormat;
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function' && format) {
      return Shopify.formatMoney(cents, format).trim();
    }
    return '€' + (cents / 100).toFixed(2).replace('.', ',');
  };

  class BFGiftBlock {
    constructor(root) {
      this.root = root;
      this.output = root.querySelector('[data-bf-gift-output]');
      this.dialog = root.querySelector('[data-bf-gift-dialog]');

      this.core = {
        price: Number(root.dataset.corePrice) || 0,
        compare: Number(root.dataset.coreCompare) || 0,
        msrp: Number(root.dataset.coreMsrp) || 0,
        isSet: root.dataset.coreIsSet === 'true',
        preorder: root.dataset.corePreorder === 'true',
        etaDisplay: root.dataset.coreEtaDisplay || '',
        etaIso: root.dataset.coreEtaIso || ''
      };

      this._bind();
      this._initSwatchSliders();
      this._initDelivery();
      this._fetchUpsells();

      // All gifts filtered out (OOS without pre-order) -> hide the empty selector
      if (!this.root.querySelector('[data-bf-gift]')) {
        this.root.querySelector('.bf-gift-block__title')?.setAttribute('hidden', '');
      }

      this._hideSingleColorBlocks();
      this._avoidPreorderDefaults();
      this.sync();
    }

    // A pre-order is never the default (client 2026-10-06), same rule as the
    // deal builder: each card moves to its first in-stock colour, and a
    // selected gift that is pre-order only gives way to the first visible
    // gift in stock. sync() only repaints the selected card, so switched
    // cards are repainted here.
    _avoidPreorderDefaults() {
      const isPreorder = (option) => option?.dataset.preorder === 'true';
      this.root.querySelectorAll('[data-bf-gift]').forEach((card) => {
        const active = this._activeOption(card);
        if (!isPreorder(active)) return;
        const inStock = card.querySelector('[data-bf-gift-option]:not([data-preorder="true"])');
        if (!inStock) return;
        active.classList.remove('is-active');
        inStock.classList.add('is-active');
        this._applyOptionToCard(card, inStock);
      });

      const selected = this._selectedCard();
      if (selected && isPreorder(this._activeOption(selected))) {
        const better = Array.from(this.root.querySelectorAll('[data-bf-gift]')).find(
          (card) => !card.hidden && !isPreorder(this._activeOption(card))
        );
        const radio = better?.querySelector('[data-bf-gift-radio]');
        if (radio) {
          radio.checked = true;
          this.root.querySelectorAll('[data-bf-gift]').forEach((c) => c.classList.toggle('is-selected', c === better));
        }
      }
    }

    // Swiper on the swatch rows — the same mechanism as the PDP colour
    // swatches (product-info__variations-swiper): drag/touch + both arrows,
    // arrows auto-hide when everything fits (watchOverflow).
    _initSwatchSliders() {
      if (typeof Swiper === 'undefined') {
        window.addEventListener('load', () => this._initSwatchSliders(), { once: true });
        return;
      }
      this.root.querySelectorAll('[data-bf-swatches]:not([data-bf-swiper-init])').forEach((wrap) => {
        wrap.setAttribute('data-bf-swiper-init', 'true');
        new Swiper(wrap, {
          slidesPerView: 'auto',
          spaceBetween: 8,
          watchOverflow: true,
          observer: true,
          observeParents: true,
          navigation: {
            nextEl: wrap.querySelector('[data-bf-swatches-next]'),
            prevEl: wrap.querySelector('[data-bf-swatches-prev]')
          }
        });
      });
    }

    // A card with only one buyable colour left (after OOS/exclusion filtering
    // in Liquid) has nothing to pick — hide its "Kleur:" swatch row entirely.
    _hideSingleColorBlocks() {
      this.root.querySelectorAll('[data-bf-gift]').forEach((card) => {
        const swatches = card.querySelectorAll('[data-bf-gift-option]');
        if (swatches.length <= 1) card.querySelector('.bf-gift__colors')?.setAttribute('hidden', '');
      });
    }

    /* ---------- upsells (client-side complementary recommendations) ---------- */

    async _fetchUpsells() {
      const container = this.root.querySelector('[data-bf-upsells]');
      const list = this.root.querySelector('[data-bf-upsells-list]');
      if (!container || !list) return;

      const limit = Number(container.dataset.limit) || 4;
      let items = [];
      try {
        // Section Rendering API instead of the JSON endpoint: the card markup
        // lives in sections/bf-upsell-items.liquid, which reads the
        // product.upsell_description metafield — the JSON API can't.
        const response = await fetch(
          `${Shopify.routes.root}recommendations/products?section_id=bf-upsell-items&product_id=${container.dataset.productId}&limit=${limit + 4}&intent=complementary`
        );
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        items = Array.from(doc.querySelectorAll('.bf-upsell')).slice(0, limit);
      } catch (error) {
        items = [];
      }

      list.innerHTML = '';
      items.forEach((item) => list.appendChild(item));

      container.hidden = items.length === 0;
      this._syncUpsellOutput();
    }

    _syncUpsellOutput() {
      const output = this.root.querySelector('[data-complementary-output]');
      if (!output) return;
      const ids = Array.from(this.root.querySelectorAll('[data-bf-upsell-checkbox]:checked')).map((checkbox) =>
        Number(checkbox.value)
      );
      output.value = JSON.stringify(ids);
    }

    /* ---------- state helpers ---------- */

    _selectedCard() {
      return this.root.querySelector('[data-bf-gift] [data-bf-gift-radio]:checked')?.closest('[data-bf-gift]') || null;
    }

    _activeOption(card) {
      if (!card) return null;
      return card.querySelector('[data-bf-gift-option].is-active') || card.querySelector('[data-bf-gift-option]');
    }

    /* ---------- events ---------- */

    _bind() {
      this.root.addEventListener('change', (event) => {
        if (event.target.matches('[data-bf-gift-radio]')) {
          this.root.querySelectorAll('[data-bf-gift]').forEach((card) => {
            card.classList.toggle('is-selected', card.contains(event.target));
          });
          this.sync();
        }
      });

      this.root.addEventListener('click', (event) => {
        const swatch = event.target.closest('[data-bf-gift-option]');
        if (swatch) {
          const card = swatch.closest('[data-bf-gift]');
          card.querySelectorAll('[data-bf-gift-option]').forEach((el) => el.classList.remove('is-active'));
          swatch.classList.add('is-active');
          const radio = card.querySelector('[data-bf-gift-radio]');
          if (!radio.checked) {
            radio.checked = true;
            this.root.querySelectorAll('[data-bf-gift]').forEach((c) => c.classList.toggle('is-selected', c === card));
          }
          this._applyOptionToCard(card, swatch);
          this.sync();
          return;
        }

        const media = event.target.closest('[data-bf-gift-media]');
        if (media) {
          event.preventDefault();
          const card = media.closest('[data-bf-gift]');
          const radio = card.querySelector('[data-bf-gift-radio]');
          if (radio && !radio.checked) {
            radio.checked = true;
            this.root.querySelectorAll('[data-bf-gift]').forEach((c) => c.classList.toggle('is-selected', c === card));
            this.sync();
          }
          this._openDialog(card);
          return;
        }

        if (event.target.closest('[data-bf-dialog-prev]')) {
          this._dialogStep(-1);
          return;
        }
        if (event.target.closest('[data-bf-dialog-next]')) {
          this._dialogStep(1);
          return;
        }
        const thumb = event.target.closest('[data-bf-dialog-thumb]');
        if (thumb) {
          this._dialogIndex = Number(thumb.dataset.bfDialogThumb) || 0;
          this._dialogRender();
          return;
        }

        if (event.target.closest('[data-bf-gifts-more]')) {
          this.root.querySelectorAll('[data-bf-gift][hidden]').forEach((card) => (card.hidden = false));
          event.target.closest('[data-bf-gifts-more]').hidden = true;
          return;
        }

        if (event.target.closest('[data-bf-gift-dialog-close]')) {
          this.dialog?.close();
        }
      });

      this.root.addEventListener('change', (event) => {
        if (event.target.matches('[data-bf-upsell-checkbox]')) {
          this._syncUpsellOutput();
          this.sync();
        }
      });

      this.dialog?.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });

      // The page must not scroll behind the open popup (native <dialog>
      // does not lock it) — lock is released on close/Esc via the native
      // 'close' event.
      this.dialog?.addEventListener('close', () => {
        document.documentElement.classList.remove('bf-dialog-lock');
      });

      this._bindDialogSwipe();
    }

    /* Finger/mouse swipe on the popup image steps through the gallery. */
    _bindDialogSwipe() {
      const main = this.dialog?.querySelector('.bf-popup__main');
      if (!main) return;
      let startX = 0;
      let startY = 0;
      let tracking = false;
      main.addEventListener('pointerdown', (event) => {
        tracking = true;
        startX = event.clientX;
        startY = event.clientY;
      });
      main.addEventListener('pointerup', (event) => {
        if (!tracking) return;
        tracking = false;
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          this._dialogStep(dx < 0 ? 1 : -1);
        }
      });
      main.addEventListener('pointercancel', () => {
        tracking = false;
      });
    }

    /* ---------- card presentation ---------- */

    _applyOptionToCard(card, option) {
      const image = card.querySelector('[data-bf-gift-image]');
      if (image && option.dataset.image) image.src = option.dataset.image;

      const name = card.querySelector('[data-bf-gift-name]');
      if (name && option.dataset.title) name.textContent = option.dataset.title;

      const compare = card.querySelector('[data-bf-gift-compare]');
      if (compare) compare.textContent = formatMoney(Number(option.dataset.compare) || 0);

      const colorName = card.querySelector('[data-bf-gift-color-name]');
      if (colorName) colorName.textContent = option.dataset.colorName || '';

      const eta = card.querySelector('[data-bf-gift-eta]');
      if (eta) {
        if (option.dataset.preorder === 'true' && option.dataset.etaDisplay) {
          // When the core itself is on pre-order there is no [data-bf-delivery]
          // element — the template then lives on the block root. Without this
          // fallback the gift ETA showed a bare date without the label.
          const template =
            this.root.querySelector('[data-bf-delivery]')?.dataset.preorderTemplate ||
            this.root.dataset.preorderTemplate ||
            '{date}';
          eta.textContent = template.replace('{date}', option.dataset.etaDisplay);
          eta.hidden = false;
        } else {
          eta.hidden = true;
        }
      }
    }

    /* ---------- pricing ---------- */

    _upsells() {
      return Array.from(this.root.querySelectorAll('[data-bf-upsell-checkbox]:checked')).map((checkbox) => ({
        price: Number(checkbox.dataset.price) || 0,
        compare: Number(checkbox.dataset.compare) || Number(checkbox.dataset.price) || 0
      }));
    }

    sync() {
      const card = this._selectedCard();
      const option = this._activeOption(card);
      const upsells = this._upsells();

      // Ensure the colour label reflects the active option on first paint
      if (card && option) this._applyOptionToCard(card, option);

      const giftCompare = option ? Number(option.dataset.compare) || 0 : 0;
      const upsellPrice = upsells.reduce((sum, u) => sum + u.price, 0);
      const upsellCompare = upsells.reduce((sum, u) => sum + u.compare, 0);

      const setValue = this.core.compare + giftCompare + upsellCompare;
      // MSRP rule (client-confirmed): show bundelvoordeel whenever compare − MSRP > 0,
      // regardless of set_single; the row hides itself at 0 (e.g. during a sale).
      const bundleSavings = this.core.msrp > 0 ? Math.max(0, this.core.compare - this.core.msrp) : 0;
      const total = this.core.price + upsellPrice;
      const bfDiscount = setValue - bundleSavings - total;
      const savings = setValue - total;
      // Floor, never round up — the shown % must not overstate the discount
      const savingsPct = setValue > 0 ? Math.floor((savings / setValue) * 100) : 0;

      this._setRow('set_value', setValue, formatMoney(setValue));
      this._setRow('bundle_savings', bundleSavings, '- ' + formatMoney(bundleSavings));
      this._setRow('bf_discount', bfDiscount, '- ' + formatMoney(bfDiscount));
      this._setRow('total', total, formatMoney(total));
      this._setRow('savings', savings, formatMoney(savings));
      // Intelligems price-simple variant: Bundelvoordeel + BF-korting as one row, Je voordeel as % only
      this._setRow('discount_all', savings, '- ' + formatMoney(savings));
      this._setRow('savings_pct', savings, '');
      this.root.querySelectorAll('[data-bf-pricing-pct]').forEach((pctEl) => {
        pctEl.textContent = '-' + savingsPct + '%';
      });



      // Inclusief line ([[word]] markers render highlighted)
      const incl = this.root.querySelector('[data-bf-incl]');
      if (incl) {
        const inclText = incl.querySelector('[data-bf-incl-text]') || incl;
        if (option) {
          const raw = (incl.dataset.template || '')
            .replace('{name}', option.dataset.title || '')
            .replace('{price}', formatMoney(giftCompare));
          const escaped = raw.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
          inclText.innerHTML = escaped.replace(/\[\[(.+?)\]\]/g, '<b class="bf-incl-hl">$1</b>');
          incl.hidden = false;
        } else {
          incl.hidden = true;
        }
      }

      this._syncOutput(option);
      this._syncDelivery(option);
    }

    _setRow(key, cents, formatted) {
      const row = this.root.querySelector('[data-bf-pricing-row="' + key + '"]');
      if (!row) return;
      // Rows with 0 must be hidden
      row.hidden = cents <= 0;
      const value = row.querySelector('[data-bf-pricing-value="' + key + '"]');
      if (value) value.textContent = formatted;
    }

    /* ---------- cart payload ---------- */

    _syncOutput(option) {
      if (!this.output) return;
      if (!option) {
        this.output.value = '';
        return;
      }

      const properties = { _bf_gift: 'true', _bf_role: 'gift' };
      if (option.dataset.preorder === 'true') {
        if (option.dataset.predikoId) properties._prediko_order_id = option.dataset.predikoId;
        if (option.dataset.etaIso) properties._prediko_date_iso = option.dataset.etaIso;
        // Same VISIBLE property as buy-buttons emits (key = date label,
        // value with the leading space) — identical props make identical
        // variants merge into one cart line and show the date in the cart.
        if (option.dataset.etaDisplay) {
          const label = this.root.dataset.preorderPropertyLabel || 'Verwachte leverdatum +/-';
          properties[label] = ' ' + option.dataset.etaDisplay;
        }
      }

      this.output.value = JSON.stringify({ id: Number(option.dataset.variantId), properties });
    }

    /* ---------- delivery timer ---------- */

    _initDelivery() {
      this.delivery = this.root.querySelector('[data-bf-delivery]');
      if (!this.delivery) return;
      this._deliveryInterval = setInterval(() => this._syncDelivery(this._activeOption(this._selectedCard())), 30000);
    }

    _syncDelivery(option) {
      if (!this.delivery) {
        // Core is on pre-order: buy-buttons renders the ETA line under the ATC.
        // Bump its date when the selected gift's ETA is LATER than the core's,
        // and RESTORE the original line when switching back to a colour whose
        // ETA no longer overrules it (otherwise the bumped text stayed stale).
        if (this.core.preorder) {
          const atcLine = this.root.querySelector('.product-info__custom-inventory .text-sm');
          if (atcLine) {
            if (this._atcEtaOriginal == null) this._atcEtaOriginal = atcLine.textContent;
            const giftIso = option?.dataset.preorder === 'true' ? option.dataset.etaIso || '' : '';
            if (giftIso > this.core.etaIso) {
              const template = this.root.dataset.preorderTemplate || '{date}';
              atcLine.textContent = template.replace('{date}', option.dataset.etaDisplay || '');
            } else {
              atcLine.textContent = this._atcEtaOriginal;
            }
          }
        }
        return;
      }

      // Pre-order ETA overrules the cutoff timer. ETA = the LATEST of core/gift.
      const giftIso = option?.dataset.preorder === 'true' ? option.dataset.etaIso || '' : '';
      const coreIso = this.core.preorder ? this.core.etaIso : '';
      let etaDisplay = '';
      if (coreIso || giftIso) {
        etaDisplay = giftIso > coreIso
          ? option.dataset.etaDisplay
          : this.core.etaDisplay || option?.dataset.etaDisplay || '';
      }

      if (etaDisplay) {
        const template = this.delivery.dataset.preorderTemplate || '{date}';
        this.delivery.textContent = template.replace('{date}', etaDisplay);
        this.delivery.classList.add('bf-gift-block__delivery--preorder');
        return;
      }

      const [cutoffHours, cutoffMinutes] = (this.delivery.dataset.cutoff || '23:59')
        .split(':')
        .map((part) => parseInt(part, 10) || 0);
      const now = new Date();
      const cutoff = new Date(now);
      cutoff.setHours(cutoffHours, cutoffMinutes, 0, 0);

      let extraDays = 0;
      if (cutoff.getTime() <= now.getTime()) {
        cutoff.setDate(cutoff.getDate() + 1);
        extraDays = 1;
      }

      const diff = cutoff.getTime() - now.getTime();
      const hours = Math.floor(diff / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const time = (hours > 0 ? hours + 'u ' : '') + minutes + 'm';

      const deliveryDate = new Date(now);
      deliveryDate.setDate(deliveryDate.getDate() + (Number(this.delivery.dataset.days) || 1) + extraDays);
      // No deliveries on Sunday (ClickUp 1241uyj165f): a Saturday order arrives on Monday.
      if (deliveryDate.getDay() === 0) deliveryDate.setDate(deliveryDate.getDate() + 1);
      const locale = this.delivery.dataset.locale || 'nl';
      const date = deliveryDate.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });

      this.delivery.textContent = (this.delivery.dataset.template || '')
        .replace('{time}', time)
        .replace('{date}', date);
      this.delivery.classList.remove('bf-gift-block__delivery--preorder');
    }

    /* ---------- image popup ---------- */

    _openDialog(card) {
      if (!this.dialog) return;
      const option = this._activeOption(card);
      if (!option) return;

      this._dialogImages = (option.dataset.images || '').split('|').filter(Boolean);
      if (!this._dialogImages.length && option.dataset.image) this._dialogImages = [option.dataset.image];
      if (!this._dialogImages.length) return;

      this._dialogAlt = option.dataset.title || '';
      this._dialogIndex = 0;
      this._dialogRender();

      // Something in the page (focus handling / a global listener) yanked the
      // scroll position to the top when the modal opened, leaving the visitor
      // stranded there. The dialog itself is viewport-fixed, so opening it
      // must never move the page: pin the scroll position back immediately
      // and again on the next frame (covers async jumps).
      const scrollX = window.scrollX;
      const scrollY = window.scrollY;
      this.dialog.showModal();
      window.scrollTo(scrollX, scrollY);
      document.documentElement.classList.add('bf-dialog-lock');
      requestAnimationFrame(() => window.scrollTo(scrollX, scrollY));
    }

    _dialogStep(direction) {
      if (!this._dialogImages?.length) return;
      this._dialogIndex = (this._dialogIndex + direction + this._dialogImages.length) % this._dialogImages.length;
      this._dialogRender();
    }

    _dialogRender() {
      const main = this.dialog.querySelector('[data-bf-dialog-main]');
      if (main) {
        main.src = this._dialogImages[this._dialogIndex] || '';
        main.alt = this._dialogAlt || '';
      }

      const thumbs = this.dialog.querySelector('[data-bf-dialog-thumbs]');
      if (thumbs) {
        thumbs.innerHTML = '';
        this._dialogImages.forEach((url, index) => {
          const thumb = document.createElement('button');
          thumb.type = 'button';
          thumb.className = 'bf-popup__thumb' + (index === this._dialogIndex ? ' is-active' : '');
          thumb.dataset.bfDialogThumb = index;
          thumb.innerHTML = `<img src="${url}" alt="" loading="lazy">`;
          thumbs.appendChild(thumb);
        });
      }
    }
  }

  const init = (scope) => {
    (scope || document).querySelectorAll('[data-bf-gift-block]').forEach((root) => {
      if (!root.dataset.bfGiftBlockReady) {
        root.dataset.bfGiftBlockReady = 'true';
        new BFGiftBlock(root);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
